import React from "react";
import { Flex, Card, Button } from "@jsq/ds/core";
import classNames from "classnames";
import { useIsXLScreenLogin } from "apps/login/LoginPage/hooks/useIsXLScreenLogin";
import AccountActivationCardHeader from "../../AccountActivationCardHeader";
import styles from "./styles.scss";
import { type BaseSetUpTwoFAProps } from "../types";

export interface SetupCardProps extends BaseSetUpTwoFAProps {
  /**
   * A title for the current 2FA method.
   */
  methodTitle: string;

  /**
   * A subtitle to provide extra context for the 2FA method.
   */
  methodSubtitle: string;

  /**
   * The text for the card submit button
   */
  submitButtonText: string;
  /**
   * color of the submit button text
   */
  submitButtonTextColor?: string;
  /**
   * color of the submit button
   */
  submitButtonBackgroundColor?: string;

  /**
   * The text for the "try another method" link/button.
   */
  switch2FAMethodButtonText?: string;

  /**
   * Whether or not the submit button should be in a loading state.
   */
  isSubmitting?: boolean;

  /**
   * Submit behaviour for component
   */
  onSubmit: (() => void) | ((() => Promise<void>) & (() => Promise<unknown>));

  /**
   * Variant content displayed in the card
   */
  children?: React.ReactNode;

  /**
   * Option to disable the submit button
   */
  isSubmitButtonDisabled?: boolean;

  /**
   * Option to hide/show top header
   */
  showHeader?: boolean;

  /**
   * Is rendered on a split screen
   */
  isSplitScreen?: boolean;

  /**
   * Optional footer to display at bottom of card
   */
  footer?: React.ReactNode;
}

/**
 * A reusable card to extend new 2FA setup components variants.
 * Children will typically be a form or some content explaining the 2FA method.
 */
export const SetupCard = ({
  onSwitch2FAMethodButtonClick,
  methodTitle,
  methodSubtitle,
  submitButtonText,
  switch2FAMethodButtonText,
  isSubmitting,
  onSubmit,
  children,
  submitButtonBackgroundColor,
  submitButtonTextColor,
  isSubmitButtonDisabled = false,
  showHeader = true,
  isSplitScreen,
  footer,
}: SetupCardProps) => {
  const xl = useIsXLScreenLogin();
  const title = () => {
    const Component = xl ? "h1" : "h2";
    return (
      <Component
        className={`${Component} align-center`}
        data-cy="account-activation-card-header"
        data-testid="setupCardMethodTitle"
      >
        {methodTitle}
      </Component>
    );
  };
  return (
    <Card
      className={classNames(styles.setUpTwoFA__card, {
        [styles.setUpTwoFA__card__split]: isSplitScreen,
      })}
    >
      <Flex flexDirection="column">
        {showHeader && <AccountActivationCardHeader />}
        <Flex
          className="stacked-sm"
          alignItems="center"
          justifyContent="center"
          data-cy="setup-card-title"
          data-testid="setup-card-title"
        >
          {title()}
        </Flex>
        <Flex alignItems="center" justifyContent="center">
          <p
            className={classNames({
              "text-lg": xl,
              "text-md": !xl,
              secondary: true,
              "stacked-xl": xl,
              "stacked-lg": !xl,
              "align-center": true,
            })}
            data-cy="account-activation-card-subtitle"
            data-testid="setupCardMethodSubtitle"
          >
            {methodSubtitle}
          </p>
        </Flex>
        {children}
        <Button
          className={classNames("align-center", styles.setUpTwoFA__button, {
            "stacked-md": !!onSwitch2FAMethodButtonClick,
            // use md when there is the alternative method button
            // (button has built in padding so needs less margin spacing)
            "stacked-lg": !!footer,
            // use lg when there is footer text
          })}
          isFullWidth
          isLoading={isSubmitting}
          onClick={onSubmit}
          disabled={isSubmitButtonDisabled}
          variant={submitButtonBackgroundColor || submitButtonTextColor ? "custom" : "primary"}
          backgroundColor={submitButtonBackgroundColor || undefined}
          textColor={submitButtonTextColor || undefined}
          data-cy="submit-2fa-method"
          data-testid="setupCardSubmitButton"
        >
          {submitButtonText}
        </Button>
        {!!onSwitch2FAMethodButtonClick && (
          <Button
            className="align-center"
            variant="link"
            isFullWidth
            onClick={onSwitch2FAMethodButtonClick}
            data-cy="try-another-method-auth"
          >
            {switch2FAMethodButtonText}
          </Button>
        )}
        {footer}
      </Flex>
    </Card>
  );
};
